import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useUpdateProject } from "@/hooks/useProjects";
import { formatCurrency as fmtCurrency } from "@/lib/formatCurrency";
import {
  COST_LABELS,
  BENEFIT_TYPE_LABELS,
  BENEFIT_TYPE_DESCRIPTIONS,
  calculateTotalCosts,
  calculateTotalBenefits,
  calculateROI,
  calculateBCR,
  calculatePaybackMonths,
  type ROICostStructure,
  type ROIBenefitItem,
  type BenefitType,
} from "@/data/roiModelData";

interface Props {
  projectId: string;
  roiData: any | null | undefined;
  currency: string;
  onCancel: () => void;
  onSaved?: () => void;
}

const EMPTY_COSTS: ROICostStructure = { subscriptions: 0, tokenConsumption: 0, personnel: 0, training: 0 };

export function ProjectRoiEditor({ projectId, roiData, currency, onCancel, onSaved }: Props) {
  const updateProject = useUpdateProject();
  const [saving, setSaving] = useState(false);

  const [costs, setCosts] = useState<ROICostStructure>({
    ...EMPTY_COSTS,
    ...(roiData?.costs && typeof roiData.costs === "object" && !Array.isArray(roiData.costs) ? roiData.costs : {}),
  });
  const [benefits, setBenefits] = useState<ROIBenefitItem[]>(
    Array.isArray(roiData?.benefits) && roiData.benefits.length > 0
      ? roiData.benefits.map((b: any) => ({
          type: (b.type || "other") as BenefitType,
          amount: Number(b.amount) || 0,
          description: b.description || b.label || "",
        }))
      : [{ type: "time_savings", amount: 0, description: "" }],
  );

  const totalCosts = calculateTotalCosts(costs);
  const totalBenefits = calculateTotalBenefits(benefits);
  const roi = calculateROI(totalBenefits, totalCosts);
  const bcr = calculateBCR(totalBenefits, totalCosts);
  const payback = calculatePaybackMonths(totalBenefits, totalCosts);
  const fmt = (n: number) => fmtCurrency(n, currency);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProject.mutateAsync({
        id: projectId,
        roi_data: { costs, benefits } as any,
      });
      toast.success("Datos de ROI guardados");
      onSaved?.();
    } catch (err: any) {
      toast.error(err?.message || "No se pudieron guardar los datos de ROI");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">Costos anuales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(Object.keys(COST_LABELS) as (keyof ROICostStructure)[]).map((key) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-sm">{COST_LABELS[key].name}</Label>
                <p className="text-xs text-muted-foreground">{COST_LABELS[key].description}</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min={0}
                    className="pl-7 text-sm"
                    value={costs[key] || ""}
                    onChange={(e) => setCosts((p) => ({ ...p, [key]: Number(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-success">Beneficios anuales</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBenefits((p) => [...p, { type: "other", amount: 0, description: "" }])}
            >
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {benefits.map((b, i) => (
              <div key={i} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Select
                    value={b.type}
                    onValueChange={(v) =>
                      setBenefits((p) => p.map((x, idx) => (idx === i ? { ...x, type: v as BenefitType } : x)))
                    }
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(BENEFIT_TYPE_LABELS) as BenefitType[]).map((t) => (
                        <SelectItem key={t} value={t}>{BENEFIT_TYPE_LABELS[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {benefits.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive shrink-0"
                      onClick={() => setBenefits((p) => p.filter((_, idx) => idx !== i))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{BENEFIT_TYPE_DESCRIPTIONS[b.type]}</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min={0}
                    className="pl-7 text-sm"
                    disabled={b.type === "not_defined"}
                    value={b.amount || ""}
                    onChange={(e) =>
                      setBenefits((p) => p.map((x, idx) => (idx === i ? { ...x, amount: Number(e.target.value) || 0 } : x)))
                    }
                  />
                </div>
                <Textarea
                  placeholder="Descripción del beneficio"
                  className="text-sm"
                  rows={2}
                  value={b.description}
                  onChange={(e) =>
                    setBenefits((p) => p.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))
                  }
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-muted/30">
        <CardContent className="grid grid-cols-2 sm:grid-cols-5 gap-4 py-5 text-center">
          <div>
            <p className="text-lg font-bold text-destructive">{fmt(totalCosts)}</p>
            <p className="text-xs text-muted-foreground">Costos</p>
          </div>
          <div>
            <p className="text-lg font-bold text-success">{fmt(totalBenefits)}</p>
            <p className="text-xs text-muted-foreground">Beneficios</p>
          </div>
          <div>
            <p className={`text-lg font-bold ${roi >= 0 ? "text-success" : "text-destructive"}`}>{roi.toFixed(1)}%</p>
            <p className="text-xs text-muted-foreground">ROI</p>
          </div>
          <div>
            <p className="text-lg font-bold text-foreground">{bcr.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">BCR</p>
          </div>
          <div>
            <p className="text-lg font-bold text-foreground">
              {payback === Infinity ? "∞" : payback.toFixed(1)}
            </p>
            <p className="text-xs text-muted-foreground">Payback (meses)</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          <X className="w-4 h-4 mr-1.5" /> Cancelar
        </Button>
        <Button className="gradient-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Guardar ROI
        </Button>
      </div>
    </div>
  );
}
